import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logo from "@/assets/macrohub-logo.png";
import { z } from "zod";
import { useTheme } from "next-themes";
import { Helmet } from "react-helmet-async";

// Validation schema
const authSchema = z.object({
  email: z.string().trim().email({
    message: "Invalid email address"
  }).max(255, {
    message: "Email must be less than 255 characters"
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters"
  }).max(128, {
    message: "Password must be less than 128 characters"
  }).regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, {
    message: "Password must contain uppercase, lowercase, and number"
  })
});
const Auth = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(searchParams.get("mode") === "signup");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setTheme } = useTheme();

  useEffect(() => {
    // Force dark mode on auth page
    setTheme("dark");
  }, [setTheme]);

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      if (session) {
        navigate("/vault");
      }
    });
  }, [navigate]);

  useEffect(() => {
    // Listen for auth state changes (including OAuth callbacks)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
        navigate('/vault', { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);
  const handleLogoClick = () => {
    navigate(-1);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const emailValidation = z.string().trim().email({ message: "Invalid email address" });
      const result = emailValidation.safeParse(email);
      if (!result.success) {
        toast.error("Please enter a valid email address");
        setLoading(false);
        return;
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=reset`
      });
      
      if (error) throw error;
      toast.success("Password reset email sent! Check your inbox.");
      setIsForgotPassword(false);
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Validate input
      const validationData = { email, password };
      const result = authSchema.safeParse(validationData);
      if (!result.success) {
        const errors = result.error.errors.map(err => err.message).join(", ");
        toast.error(errors);
        setLoading(false);
        return;
      }
      if (isSignUp) {
        const {
          error
        } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/vault`
          }
        });
        if (error) throw error;
        
        // Mark that this is a new user who should see onboarding
        localStorage.setItem('startOnboarding', 'true');
        
        toast.success("Account created successfully! You can now log in.");
        setIsSignUp(false);
      } else {
        const {
          error
        } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (error) throw error;
        toast.success("Signed in successfully!");
        navigate("/vault");
      }
    } catch (error: any) {
      // Handle invalid credentials error with helpful message
      if (error.message?.toLowerCase().includes("invalid login credentials")) {
        toast.error("Invalid email or password. Please check your credentials or sign up for a new account.");
      } else {
        toast.error(error.message || "An error occurred");
      }
    } finally {
      setLoading(false);
    }
  };
  const handleOAuthSignIn = async (provider: "github" | "google" | "azure") => {
    try {
      // Mark that user might be new (we'll verify on the vault page)
      localStorage.setItem('checkNewUser', 'true');
      
      const {
        error
      } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/vault`
        }
      });
      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    }
  };
  return <div className="min-h-screen bg-[#1a1a1a] flex items-start md:items-center justify-center px-4 pt-4 md:pt-0 pb-32 overflow-y-auto">
      <Helmet>
        <title>{isForgotPassword ? "Reset Password" : isSignUp ? "Sign Up" : "Sign In"} - MacroVault</title>
        <meta name="description" content={isSignUp ? "Create your MacroVault account to save and share Excel VBA macros" : "Sign in to your MacroVault account"} />
        <link rel="canonical" href="https://findmacros.com/auth" />
      </Helmet>
      <div className="w-full max-w-md space-y-4 md:space-y-8 my-auto mb-8">
        <div className="text-center">
          <button 
            onClick={handleLogoClick}
            className="inline-flex items-center gap-3 mb-8 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <img src={logo} alt="MacroVault" className="h-10 w-10" />
          </button>
          <h1 className="text-4xl font-bold text-white mb-8">
            {isForgotPassword ? "Reset your password" : isSignUp ? "Create your account" : "Welcome back"}
          </h1>
        </div>

        {isForgotPassword ? (
          <form onSubmit={handleForgotPassword} className="space-y-6">
            <p className="text-gray-400 text-center">Enter your email address and we'll send you a link to reset your password.</p>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white text-base">
                Email
              </Label>
              <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="Email" className="h-14 bg-[#2a2a2a] border-[#3a3a3a] text-white placeholder:text-gray-500 text-base" />
            </div>

            <Button type="submit" className="w-full h-14 bg-white text-black hover:bg-muted text-base font-semibold" disabled={loading}>
              {loading ? "Sending..." : "Send reset link"}
            </Button>

            <div className="text-center">
              <button type="button" onClick={() => setIsForgotPassword(false)} className="text-gray-400 hover:text-white text-sm">
                ← Back to sign in
              </button>
            </div>
          </form>
        ) : (
          <>
        <div className="space-y-4">
          <Button variant="outline" onClick={() => handleOAuthSignIn("google")} type="button" className="w-full h-14 bg-[#2a2a2a] border-[#3a3a3a] hover:bg-[#333333] text-white text-base">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </Button>

          <Button variant="outline" onClick={() => handleOAuthSignIn("azure")} type="button" className="w-full h-14 bg-[#2a2a2a] border-[#3a3a3a] hover:bg-[#333333] text-white text-base">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23" fill="none">
              <path d="M0 0h10.931v10.931H0z" fill="#f25022" />
              <path d="M12.069 0H23v10.931H12.069z" fill="#7fba00" />
              <path d="M0 12.069h10.931V23H0z" fill="#00a4ef" />
              <path d="M12.069 12.069H23V23H12.069z" fill="#ffb900" />
            </svg>
            Continue with Microsoft
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-[#3a3a3a]" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-[#1a1a1a] px-2 text-gray-400">OR</span>
          </div>
        </div>

        <form onSubmit={handleAuth} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white text-base">
              Email
            </Label>
            <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="Email" className="h-14 bg-[#2a2a2a] border-[#3a3a3a] text-white placeholder:text-gray-500 text-base" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-white text-base">
                Password
              </Label>
              {!isSignUp && (
                <button 
                  type="button" 
                  onClick={() => setIsForgotPassword(true)} 
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Forgot password?
                </button>
              )}
            </div>
            <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Password" className="h-14 bg-[#2a2a2a] border-[#3a3a3a] text-white placeholder:text-gray-500 text-base" />
          </div>

          <Button type="submit" className="w-full h-14 bg-white text-black hover:bg-muted text-base font-semibold" disabled={loading}>
            {loading ? "Loading..." : "Continue"}
          </Button>

          {isSignUp && <p className="text-xs text-gray-400 text-center mt-3">
              By continuing, you agree to MacroVault's{" "}
              <a href="/terms" className="underline hover:text-white">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" className="underline hover:text-white">
                Privacy Policy
              </a>
              .
            </p>}
        </form>

        <div className="text-center text-base text-gray-400">
          {isSignUp ? "Already have an account? " : "Don't have an account? "}
          <button type="button" onClick={() => setIsSignUp(!isSignUp)} className="text-white underline hover:no-underline">
            {isSignUp ? "Log in" : "Sign up"}
          </button>
        </div>
          </>
        )}
      </div>
    </div>;
};
export default Auth;